import { RudderstackIntegrations } from 'constants/common'
import { pageRoutes } from 'constants/routing'
import { useIsOsanoCmpActive } from 'context/common/OsanoCmpContext'
import { loadRudderAnalyticsSdk } from 'lib/loadRudderAnalyticsSdk'
import { subscribeToOsanoAnalyticsConsent } from 'lib/osanoAnalyticsConsent'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import {
  checkPathIsProviderProfileURL,
  checkPathIsSEFriendlyURL,
} from 'utils/common'

type RudderstackHookReturnType = {
  rudderStackLoaded: boolean
  analyticsConsentGranted: boolean
}

/**
 * Must render under OsanoCmpProviderSyncedToAuth so consent re-evaluates when
 * login/logout flips Osano on/off (e.g. support → anonymous).
 */
const useRudderStackInit = (): RudderstackHookReturnType => {
  const pathname = usePathname()
  const isOsanoCmpActive = useIsOsanoCmpActive()

  const [rudderLoadedForPage, setRudderLoadedForPage] = useState<string>(null)
  const [rudderStackLoaded, setRudderStackLoaded] = useState<boolean>(false)
  const [
    analyticsConsentGranted,
    setAnalyticsConsentGranted,
  ] = useState<boolean>(false)

  useEffect(
    () => subscribeToOsanoAnalyticsConsent(setAnalyticsConsentGranted),
    [isOsanoCmpActive]
  )

  useEffect(() => {
    let cancelled = false

    if (!analyticsConsentGranted) {
      delete globalThis.rudderanalytics
      setRudderStackLoaded(false)
      setRudderLoadedForPage(null)

      return () => {
        cancelled = true
      }
    }

    if (globalThis.rudderanalytics) {
      setRudderStackLoaded(true)

      return () => {
        cancelled = true
      }
    }

    void loadRudderAnalyticsSdk()
      .then(({ RudderAnalytics }) => {
        if (cancelled) {
          return
        }

        globalThis.rudderanalytics = new RudderAnalytics()
        setRudderStackLoaded(true)
      })
      .catch(() => {
        if (!cancelled) {
          setRudderStackLoaded(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [analyticsConsentGranted])

  useEffect(() => {
    if (analyticsConsentGranted && rudderStackLoaded && pathname) {
      loadRudderStack(globalThis.rudderanalytics)
    }
  }, [pathname, rudderStackLoaded, analyticsConsentGranted])

  const loadRudderStack = (rudderanalytics) => {
    const basePath = pathname.split('?')[0]

    if (rudderLoadedForPage === basePath) {
      return
    }

    setRudderLoadedForPage(basePath)

    let load3rdPartyIntegrations = false

    if (
      basePath === pageRoutes.PROVIDER_SEARCH ||
      checkPathIsProviderProfileURL(pathname) ||
      checkPathIsSEFriendlyURL(pathname)
    ) {
      load3rdPartyIntegrations = true
    }

    if (
      process.env.NEXT_PUBLIC_RUDDER_TOKEN &&
      process.env.NEXT_PUBLIC_RUDDER_DATAPLANE_URL
    ) {
      rudderanalytics.load(
        process.env.NEXT_PUBLIC_RUDDER_TOKEN,
        process.env.NEXT_PUBLIC_RUDDER_DATAPLANE_URL,
        {
          integrations: {
            [RudderstackIntegrations.GOOGLE_ADS]: load3rdPartyIntegrations,
            [RudderstackIntegrations.FACEBOOK_PIXEL]: load3rdPartyIntegrations,
          },
        }
      )
    }
  }

  return { rudderStackLoaded, analyticsConsentGranted }
}

export default useRudderStackInit
